// ─── How it works — Các Engine chi tiết ───────────────────

import { SectionTitle, Card, Pill, ListItem } from './shared';
import { CopyTradeFlowDiagram, TrailingStopFlowDiagram } from './EngineFlowDiagrams';

export function EnginesSection() {
  const engines = [
    {
      icon: 'ri-radar-line',
      color: 'text-accent-400',
      name: 'Chin Bot Engine',
      tag: 'Phân tích thị trường',
      desc: 'Engine trung tâm theo dõi buffer nến H1 theo từng tick, tính 6 chỉ báo và phát hiện 4 loại tín hiệu, sau đó gửi cảnh báo Telegram.',
      points: [
        'Buffer 100+ nến H1 cho mỗi symbol, nến mới nhất (A0) được cập nhật liên tục.',
        '4 loại phát hiện: TMA50 Cross, MA Convergence (Tụ Tách), Box, Box vs TMA50.',
        'Có cơ chế chống spam: mỗi sự kiện chỉ bắn 1 lần cho tới chu kỳ tiếp theo.',
      ],
    },
    {
      icon: 'ri-shield-check-line',
      color: 'text-primary-400',
      name: 'Trailing Stop Engine',
      tag: 'Quản lý vị thế',
      desc: 'Theo dõi các lệnh đang mở trên cTrader và tự động đặt/dời Stop Loss theo lợi nhuận. Chỉ áp dụng cho cặp Forex.',
      points: [
        'STEP 1: tự set SL & TP ban đầu cho lệnh chưa có (chỉ Forex).',
        'STEP 2: khi lợi nhuận đạt ngưỡng, SL dời về hòa vốn (+1 pip) rồi tịnh tiến từng bước.',
        '3 chế độ: Setup Only (chỉ set SL/TP), Simple (hòa vốn), Full (trailing từng bước).',
      ],
    },
    {
      icon: 'ri-swap-line',
      color: 'text-emerald-400',
      name: 'Copy Trade Engine',
      tag: 'Copy cTrader → MT4',
      desc: 'Quét danh sách lệnh đang mở trên cTrader, ghi thành "trạng thái mong muốn" để EA MT4 khớp theo — mở lệnh thiếu, đóng lệnh thừa, sửa SL/TP.',
      points: [
        'Dùng mô hình desired-state sync thay vì hàng đợi tín hiệu (không bị lệch khi lệnh đóng nhanh).',
        'Reconcile định kỳ 30 giây + phản hồi ngay khi có execution event.',
        'Hỗ trợ nhiều cặp source (cTrader) → target (MT4), bật/tắt từng cặp riêng.',
      ],
    },
    {
      icon: 'ri-pulse-line',
      color: 'text-secondary-400',
      name: 'Signal Engine',
      tag: 'Tín hiệu strategy',
      desc: 'Đánh giá entry/exit rules của strategy chung (generic) dựa trên chỉ báo và giá hiện tại để sinh tín hiệu BUY/SELL/NEUTRAL.',
      points: [
        'So sánh giá với SMA/HMA/TMA/Bollinger theo các điều kiện: crosses_above, crosses_below, greater_than, less_than, equal_to.',
        'Gom nhóm rule theo BUY/SELL, kết hợp logic AND/OR để ra tín hiệu cuối cùng.',
        'Tính luôn mức TP/SL dự kiến và lot size theo % rủi ro.',
      ],
    },
  ];

  return (
    <section className="space-y-5">
      <SectionTitle
        id="engines"
        icon="ri-cpu-line"
        iconBg="bg-accent-500/10"
        iconColor="text-accent-400"
        kicker="Engine"
        title="Bốn engine xử lý cốt lõi"
        subtitle="Mỗi engine là một khối logic độc lập, chạy theo từng tài khoản riêng và giữ trạng thái riêng để không đụng độ nhau."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {engines.map((e) => (
          <Card key={e.name}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-background-200/60 flex items-center justify-center shrink-0">
                <i className={`${e.icon} ${e.color} text-lg w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <div>
                <p className="text-foreground-900 text-sm font-semibold">{e.name}</p>
                <p className="text-foreground-400 text-[10px] uppercase tracking-wide font-semibold">{e.tag}</p>
              </div>
            </div>
            <p className="text-foreground-600 text-xs leading-relaxed mb-3">{e.desc}</p>
            <div className="space-y-1.5 border-t border-background-200/50 pt-3">
              {e.points.map((p, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-foreground-400 text-xs mt-0.5">•</span>
                  <p className="text-foreground-700 text-xs leading-relaxed">{p}</p>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

export function ChinBotSection() {
  const logics = [
    {
      icon: 'ri-crosshair-line',
      color: 'text-accent-400',
      name: 'TMA50 Cross',
      desc: 'Phát hiện giá cắt qua đường TMA50 khi nến mới đóng. So sánh nến A0 với nến A1: nếu giá cắt từ dưới lên trên → BUY, từ trên xuống dưới → SELL.',
      meta: 'Chạy mỗi 15 giây + force check lúc :05 mỗi giờ',
    },
    {
      icon: 'ri-contrast-2-line',
      color: 'text-primary-400',
      name: 'MA Convergence (Tụ Tách)',
      desc: 'Logic 1: khoảng cách SMA50 ↔ TMA50 nhỏ hơn ngưỡng → tụ nhau. Logic 2: cả 6 đường MA (SMA14/20/50, HMA50/100, TMA50) dồn lại gần nhau → siêu tụ.',
      meta: 'Chạy mỗi 2 giờ lúc :30 (giờ chẵn UTC)',
    },
    {
      icon: 'ri-box-3-line',
      color: 'text-emerald-400',
      name: 'Box',
      desc: 'Tính Min100/Max100 (min/max giá đóng của 100 nến gần nhất). Khi giá bid chạm gần một trong hai mức trong phạm vi x pips (mặc định 10) → cảnh báo.',
      meta: 'Tính lại mức box lúc :05 mỗi giờ, quét giá theo từng tick',
    },
    {
      icon: 'ri-line-height',
      color: 'text-secondary-400',
      name: 'Box vs TMA50',
      desc: 'So sánh TMA50 hiện tại với Min100/Max100. Nếu TMA50 nằm trong 25% khoảng (range) so với Min hoặc Max → cảnh báo.',
      meta: 'Chạy mỗi 2 giờ lúc :30 (giờ chẵn GMT+7)',
    },
  ];

  return (
    <section className="space-y-5">
      <SectionTitle
        id="chin-bot"
        icon="ri-flashlight-line"
        iconBg="bg-primary-500/10"
        iconColor="text-primary-400"
        kicker="Chin Bot"
        title="Bốn logic phát hiện tín hiệu"
        subtitle="Đây là trái tim của hệ thống. Mỗi logic chạy độc lập, có ngưỡng và chu kỳ riêng, và chỉ gửi một cảnh báo cho mỗi sự kiện."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {logics.map((l) => (
          <Card key={l.name}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-md bg-background-200/60 flex items-center justify-center shrink-0">
                <i className={`${l.icon} ${l.color} text-sm w-4 h-4 flex items-center justify-center`}></i>
              </div>
              <p className="text-foreground-900 text-sm font-semibold">{l.name}</p>
            </div>
            <p className="text-foreground-600 text-xs leading-relaxed">{l.desc}</p>
            <div className="mt-3">
              <Pill tone="neutral"><i className="ri-time-line text-[10px]"></i>{l.meta}</Pill>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <p className="text-xs font-semibold text-foreground-800 mb-3 flex items-center gap-2">
          <i className="ri-shield-flash-line text-accent-400 w-4 h-4 flex items-center justify-center"></i>
          Chống spam (anti-spam)
        </p>
        <div className="space-y-1">
          <ListItem icon="ri-checkbox-circle-line" iconColor="text-emerald-400" title="Chống bắn trùng">
            Mỗi sự kiện được gắn một khóa định danh (dedup key) dựa trên symbol + chu kỳ + hướng. Nếu cùng sự kiện bị phát hiện lại, nó sẽ bị bỏ qua.
          </ListItem>
          <ListItem icon="ri-checkbox-circle-line" iconColor="text-emerald-400" title="Chống spam lúc khởi động">
            Khi engine bắt đầu, trạng thái hiện tại được đánh dấu "đã tiêu thụ" để không bắn lại các tín hiệu cũ đã xảy ra trong lúc app tắt.
          </ListItem>
          <ListItem icon="ri-checkbox-circle-line" iconColor="text-emerald-400" title="Chống trùng giữa nhiều máy">
            Hai thiết bị hay hai tài khoản theo dõi cùng một symbol sẽ tạo ra cùng một dedup key, nên chỉ có một tin nhắn được gửi thay vì gửi đúp.
          </ListItem>
        </div>
      </Card>
    </section>
  );
}

export function CopyTradeFlowSection() {
  return (
    <section className="space-y-5">
      <SectionTitle
        id="copy-trade-flow"
        icon="ri-swap-line"
        iconBg="bg-emerald-500/10"
        iconColor="text-emerald-400"
        kicker="Copy Trade"
        title="Luồng copy từ cTrader sang MT4"
        subtitle="Copy Trade dùng mô hình desired-state sync: cTrader là nguồn sự thật duy nhất, EA MT4 chỉ việc khớp theo — không lo lệch khi lệnh đóng nhanh."
      />
      <CopyTradeFlowDiagram />
      <Card>
        <div className="space-y-1">
          <ListItem icon="ri-loop-left-line" iconColor="text-emerald-400" title="Tại sao không dùng hàng đợi tín hiệu">
            Hàng đợi kiểu "new_position / close_position" dễ để sót tín hiệu cũ (lệnh đã đóng mà tín hiệu vẫn nằm chờ). Desired-state luôn phản ánh đúng danh sách lệnh hiện tại.
          </ListItem>
          <ListItem icon="ri-time-line" iconColor="text-primary-400" title="Độ trễ">
            Execution event (~100ms) → reconcile → ghi state (~200ms). Kèm reconcile định kỳ 30 giây làm lưới an toàn.
          </ListItem>
          <ListItem icon="ri-git-branch-line" iconColor="text-secondary-400" title="Nhiều cặp copy">
            Một tài khoản nguồn có thể copy sang nhiều MT4 khác nhau. Reconcile chỉ chạy một lần cho mỗi tài khoản nguồn, rồi ghi state cho từng cặp.
          </ListItem>
        </div>
      </Card>
    </section>
  );
}

export function TrailingStopFlowSection() {
  return (
    <section className="space-y-5">
      <SectionTitle
        id="trailing-stop-flow"
        icon="ri-shield-check-line"
        iconBg="bg-primary-500/10"
        iconColor="text-primary-400"
        kicker="Trailing Stop"
        title="Luồng dời Stop Loss theo lợi nhuận"
        subtitle="Engine theo dõi từng vị thế Forex theo tick, set SL/TP ban đầu rồi dời SL về hoà vốn và tịnh tiến từng bước khi lợi nhuận tăng."
      />
      <TrailingStopFlowDiagram />
      <Card>
        <div className="space-y-1">
          <ListItem icon="ri-shield-star-line" iconColor="text-primary-400" title="Ratchet SL — chỉ tiến không lùi">
            SL chỉ được dời theo hướng có lợi (BUY dời lên, SELL dời xuống), không bao giờ kéo SL ngược lại làm mất lợi nhuận đã khoá.
          </ListItem>
          <ListItem icon="ri-timer-line" iconColor="text-secondary-400" title="Giới hạn tần suất sửa">
            Mỗi vị thế chỉ được amend-sltp cách nhau tối thiểu 2 giây, tránh spam lệnh sửa và tốn quota edge function.
          </ListItem>
          <ListItem icon="ri-flashlight-line" iconColor="text-emerald-400" title="Tự đóng khi chạm TP">
            Khi profit đạt mức takeProfit, engine tự động đóng lệnh ngay (có cơ chế chống đóng trùng với tpClosePending).
          </ListItem>
        </div>
      </Card>
    </section>
  );
}